import React, { useState, useEffect } from 'react';

const NewHomePage = ({ onStartEvaluation, onStartSelectiveTest, isAuthenticated, onShowAuth }) => {
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showWaitlistForm, setShowWaitlistForm] = useState(false);
  const [waitlistData, setWaitlistData] = useState({ name: '', email: '' });

  const handleStartEvaluation = () => {
    if (isAuthenticated) {
      onStartEvaluation();
    } else {
      onShowAuth('login');
    }
  };

  const toggleFAQ = (index) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleWaitlistSubmit = async () => {
    const { name, email } = waitlistData;

    if (!name || !email) {
      alert('Please fill in all fields');
      return;
    }

    if (!email.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/selective-waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        alert(data.message || 'Successfully joined the waitlist!');
        setWaitlistData({ name: '', email: '' });
        setShowWaitlistForm(false);
      } else {
        alert(data.detail || 'An error occurred. Please try again.');
      }
    } catch (error) {
      console.error('Error joining waitlist:', error);
      alert('An error occurred. Please try again later.');
    }
  };

  // Add Google Fonts and custom styles
  useEffect(() => {
    // Add Google Fonts (Playfair Display & DM Sans)
    const fontLink = document.createElement('link');
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;600;700&display=swap';
    fontLink.rel = 'stylesheet';
    document.head.appendChild(fontLink);

    // Add custom CSS styles to match homepage-demo.html design
    const style = document.createElement('style');
    style.textContent = `
      .font-playfair { font-family: 'Playfair Display', serif; }
      .font-dm-sans { font-family: 'DM Sans', sans-serif; }
      .text-primary { color: #1CB7C8; }
      .bg-primary { background: linear-gradient(135deg, #1CB7C8 0%, #159BA9 100%); }
      .bg-primary-solid { background-color: #1CB7C8; }
      .bg-primary-dark { background: linear-gradient(135deg, #159BA9 0%, #127A85 100%); }
      .text-charcoal { color: #1A1A1A; }
      .bg-charcoal { background-color: #1A1A1A; }
      .bg-soft-cream { background-color: #FDFBF7; }
      .bg-cream { background-color: #FDFBF7; }
      .border-primary { border-color: #1CB7C8; }
      .hover\\:bg-primary-dark:hover { background: linear-gradient(135deg, #159BA9 0%, #127A85 100%); }
      .hover\\:text-primary:hover { color: #1CB7C8; }
      .hover\\:scale-102:hover { transform: scale(1.02); }

      @keyframes float {
        0%, 100% {
          transform: translateY(0);
        }
        50% {
          transform: translateY(-10px);
        }
      }

      .hero-visual-card {
        animation: float 6s ease-in-out infinite;
      }

      .live-badge {
        display: inline-flex;
        align-items: center;
        padding: 0.375rem 0.875rem;
        background: linear-gradient(135deg, #10B981 0%, #059669 100%);
        color: white;
        border-radius: 100px;
        font-size: 0.75rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        font-family: 'DM Sans', sans-serif;
        box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
      }

      .coming-soon-badge {
        display: inline-flex;
        align-items: center;
        padding: 0.375rem 0.875rem;
        background: linear-gradient(135deg, #FFA500 0%, #FF8C00 100%);
        color: white;
        border-radius: 100px;
        font-size: 0.75rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        font-family: 'DM Sans', sans-serif;
        box-shadow: 0 2px 8px rgba(255, 165, 0, 0.3);
      }

      .hero-pattern {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        opacity: 0.03;
        background-image:
          linear-gradient(#1A1A1A 1px, transparent 1px),
          linear-gradient(90deg, #1A1A1A 1px, transparent 1px);
        background-size: 40px 40px;
        pointer-events: none;
      }

      .title-underline {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 12px;
        opacity: 0.5;
      }

      .stats-pattern {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-image:
          linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px);
        background-size: 60px 60px;
        opacity: 0.3;
        pointer-events: none;
      }
    `;
    document.head.appendChild(style);

    // Scroll animation observer
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
        }
      });
    }, observerOptions);

    document.querySelectorAll('.animate-on-scroll').forEach(el => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(30px)';
      el.style.transition = 'opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1), transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)';
      observer.observe(el);
    });

    return () => {
      if (document.head.contains(fontLink)) document.head.removeChild(fontLink);
      if (document.head.contains(style)) document.head.removeChild(style);
      observer.disconnect();
    };
  }, []);

  const faqs = [
    {
      question: 'What is MathsCampus AI-powered maths evaluation?',
      answer: 'MathsCampus delivers free AI-powered diagnostic maths assessments for students in Year 3 to Year 12. Our platform uses adaptive technology to provide personalized pathways aligned to Australian curriculum standards, helping students identify strengths and areas for improvement.'
    },
    {
      question: 'How does the diagnostic assessment work?',
      answer: 'Our AI generates 60 unique questions tailored to each student\'s year level using adaptive technology. The assessment covers key mathematical topics and provides instant diagnostic results with personalized learning recommendations.'
    },
    {
      question: 'Is MathsCampus aligned with Australian curriculum standards?',
      answer: 'Yes! All our assessments and learning paths are fully aligned with Australian curriculum mathematics standards from Year 3 to Year 12, ensuring students learn exactly what they need for their grade level.'
    },
    {
      question: 'What makes MathsCampus different from other online maths platforms?',
      answer: 'MathsCampus offers completely free diagnostic assessments with AI-powered personalized learning paths. Unlike other platforms, we don\'t require subscriptions for basic features. Plus, our Selective Exam preparation module (coming soon) provides comprehensive practice with questions based on past paper patterns covering all 4 exam types: Reading, Mathematical Reasoning, Thinking Skills, and Writing.'
    },
    {
      question: 'How does AI help improve my child\'s math learning?',
      answer: 'Our AI identifies specific knowledge gaps and provides precise insights rather than generic feedback. This enables targeted learning interventions, personalized question difficulty, and adaptive practice that evolves with your child\'s progress.'
    }
  ];

  return (
    <div className="font-dm-sans text-charcoal bg-white min-h-screen">
      {/* Navigation */}
      <header className="fixed top-0 left-0 right-0 bg-white bg-opacity-90 backdrop-blur-md border-b border-gray-200 z-50">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img src="/maths_logo_teal.png" alt="MathsCampus Logo" className="h-10 w-auto transition-transform hover:scale-105" />
              <span className="ml-3 text-2xl font-playfair font-bold text-charcoal" style={{ letterSpacing: '-0.02em' }}>MathsCampus</span>
            </div>

            <div className="hidden md:flex items-center space-x-10">
              <a href="#features" className="text-base font-medium text-gray-700 hover:text-primary transition-colors relative group">
                Features
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all group-hover:w-full"></span>
              </a>
              <a href="#testimonials" className="text-base font-medium text-gray-700 hover:text-primary transition-colors relative group">
                Success Stories
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all group-hover:w-full"></span>
              </a>
              <button
                onClick={handleStartEvaluation}
                className="px-6 py-2.5 bg-primary text-white rounded-lg font-semibold text-base transition-all hover:bg-primary-dark hover:-translate-y-0.5 shadow-md hover:shadow-lg"
              >
                Get Started
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-charcoal hover:text-primary"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"/>
                </svg>
              </button>

              {mobileMenuOpen && (
                <div className="absolute top-20 right-4 bg-white rounded-lg shadow-xl p-4 w-48 border border-gray-200">
                  <a href="#features" className="block py-2 text-charcoal hover:text-primary">Features</a>
                  <a href="#testimonials" className="block py-2 text-charcoal hover:text-primary">Success Stories</a>
                  <button
                    onClick={() => {
                      handleStartEvaluation();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full mt-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors"
                  >
                    Get Started
                  </button>
                </div>
              )}
            </div>
          </div>
        </nav>
      </header>

      <main>
        {/* Hero Section */}
        <section className="relative pt-24 pb-20 md:pt-32 md:pb-32 bg-gradient-to-br from-cream to-white overflow-hidden">
          <div className="hero-pattern"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-8 animate-on-scroll">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-full text-sm font-medium text-gray-700">
                  <span className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse"></span>
                  Supporting students from Year 3 to Year 12
                </div>

                <h1 className="text-4xl md:text-6xl lg:text-7xl font-playfair font-bold text-charcoal leading-tight" style={{ letterSpacing: '-0.03em' }}>
                  Master mathematics<br />with{' '}
                  <span className="relative inline-block text-primary">
                    clarity
                    <svg className="title-underline" viewBox="0 0 200 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 10C40 4 120 2 198 8" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                    </svg>
                  </span>
                </h1>

                <p className="text-xl text-gray-700 leading-relaxed max-w-lg">
                  From Selective Exam preparation to engaging mental math games—<br className="hidden lg:block" />
                  unlock your mathematical potential with our proven learning platform.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={handleStartEvaluation}
                    className="inline-flex items-center gap-3 px-8 py-4 bg-primary text-white rounded-xl font-semibold text-lg transition-all hover:bg-primary-dark hover:-translate-y-1 shadow-lg hover:shadow-xl"
                  >
                    Start Learning Free
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M7 4L13 10L7 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button
                    onClick={() => scrollToSection('features')}
                    className="px-8 py-4 bg-white border-2 border-gray-300 text-charcoal rounded-xl font-semibold text-lg transition-all hover:border-primary hover:text-primary hover:-translate-y-1 shadow-md hover:shadow-lg"
                  >
                    Watch Demo
                  </button>
                </div>

                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-6 sm:gap-8">
                  <div className="text-left">
                    <div className="text-4xl font-playfair font-bold text-primary leading-none mb-1">98%</div>
                    <div className="text-sm text-gray-500 font-medium">Success Rate</div>
                  </div>
                  <div className="w-px h-10 bg-gray-300"></div>
                  <div className="text-left">
                    <div className="text-4xl font-playfair font-bold text-primary leading-none mb-1">15k+</div>
                    <div className="text-sm text-gray-500 font-medium">Questions</div>
                  </div>
                  <div className="w-px h-10 bg-gray-300"></div>
                  <div className="text-left">
                    <div className="text-4xl font-playfair font-bold text-primary leading-none mb-1">24/7</div>
                    <div className="text-sm text-gray-500 font-medium">Access</div>
                  </div>
                </div>
              </div>

              <div className="relative animate-on-scroll">
                <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-200 hero-visual-card">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Question Solving</span>
                    <span className="text-xs text-green-600 font-semibold">● Active</span>
                  </div>
                  <div className="bg-gray-100 rounded-xl p-5">
                    <div className="font-playfair text-lg font-medium text-charcoal mb-4">If 3x + 7 = 22, find x</div>
                    <div className="space-y-2">
                      <div className="text-gray-700">3x = 15</div>
                      <div className="text-lg font-bold text-primary">x = 5 ✓</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-16 md:py-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 animate-on-scroll">
              <h2 className="text-5xl font-playfair font-bold text-charcoal mb-4" style={{ letterSpacing: '-0.02em' }}>
                What We Offer
              </h2>
              <p className="text-xl text-gray-700 max-w-2xl mx-auto">
                Comprehensive tools designed for Australian curriculum and Selective Exam success
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Large Selective Exam Card */}
              <div className="md:col-span-2 md:row-span-2 bg-gradient-to-br from-primary from-opacity-5 to-white border-2 border-primary rounded-2xl p-8 transition-all hover:-translate-y-2 hover:shadow-2xl animate-on-scroll">
                <div className="flex items-center justify-center w-20 h-20 bg-white border-2 border-primary rounded-2xl mb-8">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" className="text-primary">
                    <rect x="8" y="8" width="32" height="32" rx="4" stroke="currentColor" strokeWidth="2"/>
                    <path d="M16 24L22 30L32 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className="text-3xl font-playfair font-semibold text-charcoal mb-4 flex items-center gap-3 flex-wrap">
                  Selective Exam Mastery
                  <span className="live-badge">Live — Free During Launch</span>
                </h3>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  Comprehensive preparation for Reading, Mathematical Reasoning, Thinking Skills, and Writing exams with adaptive learning paths
                  and detailed performance analytics. Practice by exam type, tier, or simulate full tests.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start text-gray-700">
                    <span className="text-primary font-bold mr-3">✓</span>
                    <span>Practice questions based on past paper patterns</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <span className="text-primary font-bold mr-3">✓</span>
                    <span>All 4 exam types: Reading, Mathematical Reasoning, Thinking Skills, Writing</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <span className="text-primary font-bold mr-3">✓</span>
                    <span>Tier-based difficulty progression</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <span className="text-primary font-bold mr-3">✓</span>
                    <span>Detailed solution explanations</span>
                  </li>
                </ul>
              </div>

              {/* Other Feature Cards */}
              <div className="bg-cream border border-gray-300 rounded-2xl p-8 transition-all hover:-translate-y-2 hover:shadow-xl hover:border-primary animate-on-scroll">
                <div className="flex items-center justify-center w-14 h-14 bg-white border border-gray-300 rounded-xl mb-6">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="text-primary">
                    <path d="M8 8h16v16H8z" stroke="currentColor" strokeWidth="2" fill="none"/>
                    <path d="M12 12h8M12 16h8M12 20h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <h3 className="text-xl font-playfair font-semibold text-charcoal mb-3 flex items-center gap-3 flex-wrap">
                  Free Diagnostic Test
                  <span className="live-badge">Live</span>
                </h3>
                <p className="text-gray-700">
                  Comprehensive assessment to identify your strengths and areas for improvement. Get personalized recommendations to guide your learning journey.
                </p>
              </div>

              <div className="bg-cream border border-gray-300 rounded-2xl p-8 transition-all hover:-translate-y-2 hover:shadow-xl hover:border-primary animate-on-scroll">
                <div className="flex items-center justify-center w-14 h-14 bg-white border border-gray-300 rounded-xl mb-6">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="text-primary">
                    <path d="M6 8L16 4L26 8L16 12L6 8Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                    <path d="M6 16L16 20L26 16" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                    <path d="M6 24L16 28L26 24" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className="text-xl font-playfair font-semibold text-charcoal mb-3 flex items-center gap-3 flex-wrap">
                  Structured Learning Paths
                  <span className="live-badge">Live</span>
                </h3>
                <p className="text-gray-700">
                  Follow curated pathways through topics, building mastery step-by-step.
                </p>
              </div>

              <div className="bg-cream border border-gray-300 rounded-2xl p-8 transition-all hover:-translate-y-2 hover:shadow-xl hover:border-primary animate-on-scroll">
                <div className="flex items-center justify-center w-14 h-14 bg-white border border-gray-300 rounded-xl mb-6">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="text-primary">
                    <path d="M4 28L8 16L16 20L24 8L28 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className="text-xl font-playfair font-semibold text-charcoal mb-3 flex items-center gap-3 flex-wrap">
                  Performance Analytics
                  <span className="live-badge">Live</span>
                </h3>
                <p className="text-gray-700">
                  Track progress with detailed insights and actionable data.
                </p>
              </div>

              <div className="bg-cream border border-gray-300 rounded-2xl p-8 transition-all hover:-translate-y-2 hover:shadow-xl hover:border-primary animate-on-scroll">
                <div className="flex items-center justify-center w-14 h-14 bg-white border border-gray-300 rounded-xl mb-6">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="text-primary">
                    <rect x="4" y="4" width="24" height="24" rx="2" stroke="currentColor" strokeWidth="2"/>
                    <path d="M10 12h12M10 16h8M10 20h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <h3 className="text-xl font-playfair font-semibold text-charcoal mb-3 flex items-center gap-3 flex-wrap">
                  Practice Corner
                  <span className="live-badge">Live</span>
                </h3>
                <p className="text-gray-700">
                  Focused practice sessions aligned with the Australian curriculum. Target specific topics and build mastery through structured exercises.
                </p>
              </div>

              <div className="bg-cream border border-gray-300 rounded-2xl p-8 transition-all hover:-translate-y-2 hover:shadow-xl hover:border-primary animate-on-scroll">
                <div className="flex items-center justify-center w-14 h-14 bg-white border border-gray-300 rounded-xl mb-6">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="text-primary">
                    <circle cx="16" cy="16" r="12" stroke="currentColor" strokeWidth="2"/>
                    <path d="M16 10V16L20 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <h3 className="text-xl font-playfair font-semibold text-charcoal mb-3 flex items-center gap-3 flex-wrap">
                  Mental Math Games
                  <span className="live-badge">Live</span>
                </h3>
                <p className="text-gray-700">
                  Build speed and confidence through engaging games. Speed Arithmetic, Pattern Recognition,
                  and Math Battle make practice fun.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-16 md:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 animate-on-scroll">
              <h2 className="text-5xl font-playfair font-bold text-charcoal mb-4" style={{ letterSpacing: '-0.02em' }}>
                Package Offerings
              </h2>
              <p className="text-xl text-gray-700 max-w-2xl mx-auto">
                Start with our comprehensive free tier or join the waitlist for Selective Exam preparation
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-10 max-w-5xl mx-auto">
              {/* Free Tier */}
              <div className="bg-white border-2 border-gray-300 rounded-3xl p-8 md:p-12 transition-all hover:-translate-y-2 hover:shadow-2xl animate-on-scroll">
                <div className="mb-8">
                  <h3 className="text-3xl font-playfair font-bold text-charcoal mb-2">Free Tier</h3>
                  <p className="text-gray-700">Everything you need to excel in mathematics</p>
                </div>

                <div className="flex items-baseline gap-2 mb-8">
                  <span className="text-4xl md:text-6xl font-playfair font-bold text-primary">$0</span>
                  <span className="text-lg text-gray-500">forever</span>
                </div>

                <ul className="space-y-4 mb-10">
                  <li className="flex items-start border-b border-gray-200 pb-3.5">
                    <span className="text-primary font-bold text-xl mr-3">✓</span>
                    <span className="text-gray-700">Free Evaluation/Diagnostic Test</span>
                  </li>
                  <li className="flex items-start border-b border-gray-200 pb-3.5">
                    <span className="text-primary font-bold text-xl mr-3">✓</span>
                    <span className="text-gray-700">Learning Paths</span>
                  </li>
                  <li className="flex items-start border-b border-gray-200 pb-3.5">
                    <span className="text-primary font-bold text-xl mr-3">✓</span>
                    <span className="text-gray-700">Practice Corner</span>
                  </li>
                  <li className="flex items-start border-b border-gray-200 pb-3.5">
                    <span className="text-primary font-bold text-xl mr-3">✓</span>
                    <span className="text-gray-700">Mental Math Games</span>
                  </li>
                  <li className="flex items-start border-b border-gray-200 pb-3.5">
                    <span className="text-primary font-bold text-xl mr-3">✓</span>
                    <span className="text-gray-700">Covers Year 3 to Year 12</span>
                  </li>
                  <li className="flex items-start border-b border-gray-200 pb-3.5">
                    <span className="text-primary font-bold text-xl mr-3">✓</span>
                    <span className="text-gray-700">Aligned with Australian Curriculum</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary font-bold text-xl mr-3">✓</span>
                    <span className="text-gray-700">Progress Tracking & Analytics</span>
                  </li>
                </ul>

                <button
                  onClick={handleStartEvaluation}
                  className="w-full py-4 bg-primary text-white rounded-xl font-bold text-lg transition-all hover:bg-primary-dark hover:-translate-y-1 shadow-md hover:shadow-xl"
                >
                  Start Learning Free
                </button>
              </div>

              {/* Selective Exam Pro */}
              <div className="bg-gradient-to-br from-primary from-opacity-5 to-white border-2 border-primary rounded-3xl p-8 md:p-12 transition-all hover:-translate-y-2 hover:shadow-2xl relative animate-on-scroll">
                <div className="absolute -top-3 right-8 px-5 py-2 bg-primary text-white rounded-full text-xs font-bold uppercase tracking-wider">
                  Coming Soon
                </div>

                <div className="mb-8">
                  <h3 className="text-3xl font-playfair font-bold text-charcoal mb-2">Selective Exam Pro</h3>
                  <p className="text-gray-700">All Free features + comprehensive Selective Exam preparation</p>
                </div>

                <div className="flex items-baseline gap-2 mb-8">
                  <span className="text-4xl md:text-6xl font-playfair font-bold text-primary">TBA</span>
                </div>

                <ul className="space-y-4 mb-10">
                  <li className="flex items-start border-b border-gray-200 pb-3.5">
                    <span className="text-primary font-bold text-xl mr-3">✓</span>
                    <span className="text-gray-700">All Free Tier Features</span>
                  </li>
                  <li className="flex items-start border-b border-gray-200 pb-3.5">
                    <span className="text-primary font-bold text-xl mr-3">✓</span>
                    <span className="text-gray-700">Selective Exam Mastery Module</span>
                  </li>
                  <li className="flex items-start border-b border-gray-200 pb-3.5">
                    <span className="text-primary font-bold text-xl mr-3">✓</span>
                    <span className="text-gray-700">Reading, Mathematical Reasoning, Thinking Skills & Writing</span>
                  </li>
                  <li className="flex items-start border-b border-gray-200 pb-3.5">
                    <span className="text-primary font-bold text-xl mr-3">✓</span>
                    <span className="text-gray-700">Practice questions based on past paper patterns</span>
                  </li>
                  <li className="flex items-start border-b border-gray-200 pb-3.5">
                    <span className="text-primary font-bold text-xl mr-3">✓</span>
                    <span className="text-gray-700">Full Mock Exams</span>
                  </li>
                  <li className="flex items-start border-b border-gray-200 pb-3.5">
                    <span className="text-primary font-bold text-xl mr-3">✓</span>
                    <span className="text-gray-700">Detailed Solution Explanations</span>
                  </li>
                  <li className="flex items-start border-b border-gray-200 pb-3.5">
                    <span className="text-primary font-bold text-xl mr-3">✓</span>
                    <span className="text-gray-700">Performance Analytics</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary font-bold text-xl mr-3">✓</span>
                    <span className="text-gray-700">Priority Support</span>
                  </li>
                </ul>

                <button
                  onClick={() => setShowWaitlistForm(!showWaitlistForm)}
                  className="w-full py-4 bg-white text-primary border-2 border-primary rounded-xl font-bold text-lg transition-all hover:bg-primary hover:bg-opacity-10 hover:-translate-y-1 shadow-md hover:shadow-xl"
                >
                  {showWaitlistForm ? 'Hide Form' : 'Join Waitlist'}
                </button>

                {showWaitlistForm && (
                  <div className="mt-6 space-y-3">
                    <input
                      type="text"
                      placeholder="Your Name"
                      value={waitlistData.name}
                      onChange={(e) => setWaitlistData({ ...waitlistData, name: e.target.value })}
                      className="w-full px-5 py-3.5 border-2 border-gray-300 rounded-lg font-dm-sans text-base focus:outline-none focus:border-primary transition-colors"
                    />
                    <input
                      type="email"
                      placeholder="Your Email"
                      value={waitlistData.email}
                      onChange={(e) => setWaitlistData({ ...waitlistData, email: e.target.value })}
                      className="w-full px-5 py-3.5 border-2 border-gray-300 rounded-lg font-dm-sans text-base focus:outline-none focus:border-primary transition-colors"
                    />
                    <button
                      onClick={handleWaitlistSubmit}
                      className="w-full py-3.5 bg-primary text-white rounded-lg font-bold transition-all hover:bg-primary-dark"
                    >
                      Join Waitlist
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Learning Paths Section */}
        <section className="py-16 md:py-32 bg-cream">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 animate-on-scroll">
              <h2 className="text-5xl font-playfair font-bold text-charcoal mb-4" style={{ letterSpacing: '-0.02em' }}>
                Learning Paths by Year Level
              </h2>
              <p className="text-xl text-gray-700 max-w-2xl mx-auto">
                Curriculum-aligned learning journeys designed specifically for Australian students.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Years 3-4 */}
              <div className="bg-white border border-gray-300 rounded-2xl p-8 transition-all hover:-translate-y-2 hover:shadow-xl animate-on-scroll">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center text-white font-bold text-xl">
                    1
                  </div>
                  <h3 className="ml-4 text-2xl font-playfair font-semibold text-charcoal">Years 3-4</h3>
                </div>
                <p className="text-gray-600 mb-6">
                  Structured learning paths covering all essential mathematical concepts for years 3-4 students.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span>Number & Algebra</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span>Measurement & Geometry</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span>Statistics & Probability</span>
                  </div>
                </div>
              </div>

              {/* Years 5-6 */}
              <div className="bg-white border border-gray-300 rounded-2xl p-8 transition-all hover:-translate-y-2 hover:shadow-xl animate-on-scroll">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center text-white font-bold text-xl">
                    2
                  </div>
                  <h3 className="ml-4 text-2xl font-playfair font-semibold text-charcoal">Years 5-6</h3>
                </div>
                <p className="text-gray-600 mb-6">
                  Structured learning paths covering all essential mathematical concepts for years 5-6 students.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span>Number & Algebra</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span>Measurement & Geometry</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span>Statistics & Probability</span>
                  </div>
                </div>
              </div>

              {/* Years 7-8 */}
              <div className="bg-white border border-gray-300 rounded-2xl p-8 transition-all hover:-translate-y-2 hover:shadow-xl animate-on-scroll">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center text-white font-bold text-xl">
                    3
                  </div>
                  <h3 className="ml-4 text-2xl font-playfair font-semibold text-charcoal">Years 7-8</h3>
                </div>
                <p className="text-gray-600 mb-6">
                  Structured learning paths covering all essential mathematical concepts for years 7-8 students.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span>Number & Algebra</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span>Measurement & Geometry</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span>Statistics & Probability</span>
                  </div>
                </div>
              </div>

              {/* Years 9-10 */}
              <div className="bg-white border border-gray-300 rounded-2xl p-8 transition-all hover:-translate-y-2 hover:shadow-xl animate-on-scroll">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center text-white font-bold text-xl">
                    4
                  </div>
                  <h3 className="ml-4 text-2xl font-playfair font-semibold text-charcoal">Years 9-10</h3>
                </div>
                <p className="text-gray-600 mb-6">
                  Structured learning paths covering all essential mathematical concepts for years 9-10 students.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span>Number & Algebra</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span>Measurement & Geometry</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span>Statistics & Probability</span>
                  </div>
                </div>
              </div>

              {/* Years 11-12 */}
              <div className="bg-white border border-gray-300 rounded-2xl p-8 transition-all hover:-translate-y-2 hover:shadow-xl animate-on-scroll">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center text-white font-bold text-xl">
                    5
                  </div>
                  <h3 className="ml-4 text-2xl font-playfair font-semibold text-charcoal">Years 11-12</h3>
                </div>
                <p className="text-gray-600 mb-6">
                  Structured learning paths covering all essential mathematical concepts for years 11-12 students.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span>Number & Algebra</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span>Measurement & Geometry</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span>Statistics & Probability</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 md:py-32 bg-primary relative overflow-hidden">
          <div className="stats-pattern"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-16 animate-on-scroll">
              <h2 className="text-5xl font-playfair font-bold text-white mb-4" style={{ letterSpacing: '-0.02em' }}>
                Proven results that speak for themselves
              </h2>
              <p className="text-xl text-white opacity-90 max-w-2xl mx-auto">
                Join thousands of students who have transformed their mathematical abilities
                and achieved their academic goals.
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              <div className="bg-white bg-opacity-10 backdrop-blur-md border border-white border-opacity-20 rounded-2xl p-10 text-center transition-all hover:bg-opacity-15 hover:-translate-y-2 animate-on-scroll">
                <div className="text-4xl md:text-6xl font-playfair font-bold text-white mb-3">Year 3-12</div>
                <div className="text-lg font-semibold text-white mb-1">Grade Coverage</div>
                <div className="text-sm text-white opacity-85">Full curriculum support</div>
              </div>

              <div className="bg-white bg-opacity-10 backdrop-blur-md border border-white border-opacity-20 rounded-2xl p-10 text-center transition-all hover:bg-opacity-15 hover:-translate-y-2 animate-on-scroll">
                <div className="text-4xl md:text-6xl font-playfair font-bold text-white mb-3">15,000+</div>
                <div className="text-lg font-semibold text-white mb-1">Practice Questions</div>
                <div className="text-sm text-white opacity-85">Adaptive learning</div>
              </div>

              <div className="bg-white bg-opacity-10 backdrop-blur-md border border-white border-opacity-20 rounded-2xl p-10 text-center transition-all hover:bg-opacity-15 hover:-translate-y-2 animate-on-scroll">
                <div className="text-4xl md:text-6xl font-playfair font-bold text-white mb-3">98%</div>
                <div className="text-lg font-semibold text-white mb-1">Success Rate</div>
                <div className="text-sm text-white opacity-85">Students achieving goals</div>
              </div>

              <div className="bg-white bg-opacity-10 backdrop-blur-md border border-white border-opacity-20 rounded-2xl p-10 text-center transition-all hover:bg-opacity-15 hover:-translate-y-2 animate-on-scroll">
                <div className="text-4xl md:text-6xl font-playfair font-bold text-white mb-3">4.9/5</div>
                <div className="text-lg font-semibold text-white mb-1">Average Rating</div>
                <div className="text-sm text-white opacity-85">From student reviews</div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section id="testimonials" className="py-16 md:py-32 bg-cream">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 animate-on-scroll">
              <h2 className="text-5xl font-playfair font-bold text-charcoal mb-4" style={{ letterSpacing: '-0.02em' }}>
                Success Stories
              </h2>
              <p className="text-xl text-gray-700 max-w-2xl mx-auto">
                What students and parents say
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white border border-gray-300 rounded-2xl p-10 transition-all hover:-translate-y-2 hover:shadow-xl animate-on-scroll">
                <div className="text-yellow-500 text-xl mb-6 tracking-wider">★★★★★</div>
                <p className="text-lg text-charcoal mb-8 leading-relaxed italic">
                  "The Practice Corner has been a game-changer for my son. The focused sessions help him target his weak areas,
                  and we can see real improvement in topics that used to challenge him."
                </p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary text-white rounded-full font-bold text-xl">
                    S
                  </div>
                  <div>
                    <div className="font-semibold text-charcoal">Sarah M.</div>
                    <div className="text-sm text-gray-500">Parent, Sydney</div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-300 rounded-2xl p-10 transition-all hover:-translate-y-2 hover:shadow-xl animate-on-scroll">
                <div className="text-yellow-500 text-xl mb-6 tracking-wider">★★★★★</div>
                <p className="text-lg text-charcoal mb-8 leading-relaxed italic">
                  "The Mental Math games are actually fun! I practice every day without even
                  realizing I'm studying. My speed has improved so much."
                </p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary text-white rounded-full font-bold text-xl">
                    J
                  </div>
                  <div>
                    <div className="font-semibold text-charcoal">James L.</div>
                    <div className="text-sm text-gray-500">Year 6 Student</div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-300 rounded-2xl p-10 transition-all hover:-translate-y-2 hover:shadow-xl animate-on-scroll">
                <div className="text-yellow-500 text-xl mb-6 tracking-wider">★★★★★</div>
                <p className="text-lg text-charcoal mb-8 leading-relaxed italic">
                  "Finally, a platform that understands the Australian curriculum. The tier-based
                  system helped us focus on exactly what we needed."
                </p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary text-white rounded-full font-bold text-xl">
                    R
                  </div>
                  <div>
                    <div className="font-semibold text-charcoal">Rajesh K.</div>
                    <div className="text-sm text-gray-500">Parent, Melbourne</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 md:py-32 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 animate-on-scroll">
              <h2 className="text-5xl font-playfair font-bold text-charcoal mb-4" style={{ letterSpacing: '-0.02em' }}>
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-700">
                Everything you need to know about MathsCampus
              </p>
            </div>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className={`bg-white border-2 border-gray-300 rounded-2xl overflow-hidden transition-all hover:border-primary hover:shadow-lg animate-on-scroll ${expandedFAQ === index ? 'border-primary shadow-lg' : ''}`}>
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full px-8 py-6 text-left flex justify-between items-center cursor-pointer"
                  >
                    <h3 className="font-playfair text-xl font-semibold text-charcoal pr-4">{faq.question}</h3>
                    <svg
                      className={`flex-shrink-0 w-6 h-6 text-primary transition-transform ${expandedFAQ === index ? 'rotate-180' : ''}`}
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path d="M19 9L12 16L5 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <div
                    className="overflow-hidden transition-all"
                    style={{
                      maxHeight: expandedFAQ === index ? '500px' : '0',
                      transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    <p className="px-8 pb-6 text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-32 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-br from-primary from-opacity-5 to-white border-2 border-primary rounded-3xl p-8 md:p-16 text-center animate-on-scroll">
              <h2 className="text-3xl md:text-5xl font-playfair font-bold text-charcoal mb-4 leading-tight" style={{ letterSpacing: '-0.02em' }}>
                Start your journey to mathematical excellence
              </h2>
              <p className="text-xl text-gray-700 mb-10 max-w-2xl mx-auto leading-relaxed">
                Join MathsCampus today and experience the difference personalized,
                comprehensive math education can make.
              </p>
              <button
                onClick={handleStartEvaluation}
                className="inline-flex items-center gap-3 px-12 py-5 bg-primary text-white rounded-xl font-bold text-lg transition-all hover:bg-primary-dark hover:-translate-y-1 shadow-lg hover:shadow-2xl"
              >
                Get Started Free
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M7 4L13 10L7 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <div className="text-sm text-gray-500 mt-4">
                ✓ No credit card required  ✓ free evaluations  ✓ Instant detailed results
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-charcoal text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-5 gap-12 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center mb-4">
                <img src="/maths_logo_teal.png" alt="MathsCampus Logo" className="h-10 w-auto" />
                <span className="ml-3 text-2xl font-playfair font-bold">MathsCampus</span>
              </div>
              <p className="text-white opacity-70 leading-relaxed">
                Empowering students to achieve mathematical excellence.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 md:col-span-3">
              <div>
                <h4 className="text-sm font-semibold uppercase tracking-wider mb-4 text-white opacity-90">Platform</h4>
                <div className="space-y-3">
                  <a href="#features" className="block text-white opacity-70 hover:opacity-100 transition-opacity">Features</a>
                  <a href="#" className="block text-white opacity-70 hover:opacity-100 transition-opacity">Pricing</a>
                  <a href="#testimonials" className="block text-white opacity-70 hover:opacity-100 transition-opacity">Success Stories</a>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold uppercase tracking-wider mb-4 text-white opacity-90">Resources</h4>
                <div className="space-y-3">
                  <a href="#" className="block text-white opacity-70 hover:opacity-100 transition-opacity">About Us</a>
                  <a href="mailto:support@mathscampus.com" className="block text-white opacity-70 hover:opacity-100 transition-opacity">Contact Us</a>
                  <a href="#faq" className="block text-white opacity-70 hover:opacity-100 transition-opacity">FAQ</a>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold uppercase tracking-wider mb-4 text-white opacity-90">Legal</h4>
                <div className="space-y-3">
                  <a href="https://www.mathscampus.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="block text-white opacity-70 hover:opacity-100 transition-opacity">Privacy Policy</a>
                  <a href="https://www.mathscampus.com/terms-of-service" target="_blank" rel="noopener noreferrer" className="block text-white opacity-70 hover:opacity-100 transition-opacity">Terms of Service</a>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-white border-opacity-10 text-center">
            <p className="text-white opacity-60 text-sm">
              © 2025 MathsCampus. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NewHomePage;
